import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

import { adManagerBulkCreateApplications, adManagerGetJob } from './api';

type Props = ModalProps<{
  applicationIDs: string[];
  jobID: string;
}>;

export const ADManagerBulkCreateUsersModal = (props: Props) => {
  const { t } = useStore();
  const { closeModal, applicationIDs, jobID } = props;
  const { showSuccess, showError } = useToaster();

  const jobQuery = useQuery({
    queryKey: ['ad-manager-job', jobID],
    queryFn: () => adManagerGetJob(jobID),
  });
  const client = useQueryClient();
  const createUsers = useMutation(() => adManagerBulkCreateApplications(jobID, applicationIDs), {
    onSuccess: () => {
      client.invalidateQueries(['ad-manager-application']);
      showSuccess(t('integrations.admanager.applications.create.success'));
      closeModal();
    },
    onError: () => {
      showError(t('integrations.admanager.applications.create.error'));
    },
  });

  if (jobQuery.isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <Stack space={6}>
      {createUsers.isLoading && <Spinner />}
      <Text font="heading1" text={t('integrations.admanager.bulkmodal.header')} />
      <Stack space={0}>
        <Text font="semibold14" text={t('integrations.admanager.modal.template.label')} />
        <Text text={jobQuery.data?.templateName} />
      </Stack>
      <Text text={t('integrations.admanager.bulkmodal.note')} />
      <Stack space={4} orientation="horizontal" justify={'flex-end'}>
        <Button
          variant="secondary"
          text={t('common.forms.button.cancel')}
          onClick={closeModal}
          isDisabled={createUsers.isLoading}
        />
        <Button
          text={t('integrations.admanager.bulkmodal.button.submit')}
          onClick={() => createUsers.mutate()}
          isDisabled={createUsers.isLoading}
        />
      </Stack>
    </Stack>
  );
};
